/**
 * CR107: Magical UX Layer - Cursor Effects
 * 
 * Provides delightful cursor feedback with sparkles, wand effects, and gentle glows
 */

import { useEffect, useState } from "react";
import { useMagicalUX } from "./MagicalUXProvider";

interface SparkleTrail {
  id: string;
  x: number;
  y: number;
  opacity: number;
  scale: number;
}

export function MagicalCursor() {
  const { state } = useMagicalUX();
  const [sparkles, setSparkles] = useState<SparkleTrail[]>([]);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (state.cursorMode === 'normal') return;

    let animationFrame: number;
    let sparkleCounter = 0;

    const handleMouseMove = (e: MouseEvent) => {
      setCursorPos({ x: e.clientX, y: e.clientY });

      if (state.cursorMode === 'sparkle') {
        // Add new sparkle every few pixels moved
        sparkleCounter++;
        if (sparkleCounter % 8 === 0) {
          const newSparkle: SparkleTrail = {
            id: `sparkle-${Date.now()}-${Math.random()}`,
            x: e.clientX + (Math.random() - 0.5) * 20,
            y: e.clientY + (Math.random() - 0.5) * 20,
            opacity: 1,
            scale: 0.5 + Math.random() * 0.5,
          };
          
          setSparkles(prev => [...prev.slice(-8), newSparkle]);
        }
      }
    };

    const animateSparkles = () => {
      setSparkles(prev => 
        prev.map(sparkle => ({
          ...sparkle,
          opacity: sparkle.opacity - 0.05,
          scale: sparkle.scale + 0.02,
        })).filter(sparkle => sparkle.opacity > 0)
      );
      animationFrame = requestAnimationFrame(animateSparkles);
    };

    document.addEventListener('mousemove', handleMouseMove);
    animationFrame = requestAnimationFrame(animateSparkles);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrame);
    };
  }, [state.cursorMode]);

  if (state.cursorMode === 'normal') return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999]" style={{ mixBlendMode: 'screen' }}>
      {/* Cursor glow effect */}
      {state.cursorMode === 'glow' && (
        <div
          className="absolute w-8 h-8 rounded-full bg-primary/20 blur-md transition-all duration-100"
          style={{
            left: cursorPos.x - 16,
            top: cursorPos.y - 16,
            boxShadow: '0 0 20px hsl(var(--primary) / 0.3)',
          }}
        />
      )}

      {/* Wand cursor effect */}
      {state.cursorMode === 'wand' && (
        <div
          className="absolute transition-all duration-100"
          style={{
            left: cursorPos.x - 4,
            top: cursorPos.y - 4,
          }}
        >
          <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          <div className="absolute -top-1 -left-1 w-4 h-4 border border-primary/30 rounded-full animate-ping" />
        </div>
      )}

      {/* Sparkle trail */}
      {sparkles.map(sparkle => (
        <div
          key={sparkle.id}
          className="absolute w-1 h-1 bg-primary rounded-full"
          style={{
            left: sparkle.x,
            top: sparkle.y,
            opacity: sparkle.opacity,
            transform: `scale(${sparkle.scale})`,
            boxShadow: '0 0 4px hsl(var(--primary) / 0.6)',
          }}
        />
      ))}
    </div>
  );
}